import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TablerIconsModule } from 'angular-tabler-icons';
import { BillingPlan } from '../../../../core/services/scheduled-subscription-rules';
import {
  MatDialog,
  MatDialogRef,
  MatDialogActions,
  MatDialogTitle,
  MatDialogContent,
  MatDialogModule,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';

export interface FeeVatSummaryItem {
  name: string;
  billing_code: string;
  vat_rate: number | null;
}

export interface ConfirmationCreationDraftInvoiceData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'accent' | 'warn';
  cancelColor?: 'primary' | 'accent' | 'warn';
  invoiceDetails?: {
    corporate?: string;
    address?: string;
    city?: string;
    country?: string;
    vatRate?: number | string;
    sdi?: string;
  };
  feeVatSummary?: FeeVatSummaryItem[];
  showGlobalVatInput?: boolean;
  pennylaneOptions?: Array<{ name: string; country: string | null }>;
  selectedPennylaneCountry?: string | null;
  pennylaneCountryLabel?: string;
  /** What each recurring annual fee will create: first invoice and Pennylane subscription. */
  billingPlan?: BillingPlan | null;
}

export interface InvoiceCreationResult {
  confirmed: boolean;
  vatRate?: number;
  pennylane_country?: string | null;
}

@Component({
  selector: 'app-confirmation-creation-draft-invoice',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    TablerIconsModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule
  ],
  template: `
    <div class="dialog-container">
      <h5 mat-dialog-title class="dialog-title">{{ data.title }}</h5>
      
      <div mat-dialog-content>
        <div class="pennylane-current-target">
          <span class="pennylane-target-label">Pennylane target:</span>
          <span class="pennylane-target-value">Pennylane {{ getVisiblePennylaneCountryLabel() }}</span>
        </div>

        <div class="vat-section pennylane-section" *ngIf="data.pennylaneOptions && data.pennylaneOptions.length > 0">
          <label class="pennylane-label">Pennylane destination</label>
          <mat-form-field
            appearance="outline"
            class="pennylane-select-field"
            [class.pennylane-select-error]="pennylaneErrorMessage">
            <mat-select
              [(ngModel)]="data.selectedPennylaneCountry"
              (selectionChange)="onPennylaneChange()"
              placeholder="Choose a Pennylane account">
              <mat-option *ngFor="let option of data.pennylaneOptions" [value]="option.country">
                {{ option.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="message-card">
          {{ data.message }}
        </div>

        <div class="billing-plan" data-testid="draft-invoice-billing-plan" *ngIf="data.billingPlan as plan">
          <div class="billing-plan-alert" role="alert">
            <i-tabler name="alert-triangle" class="billing-plan-alert-icon"></i-tabler>
            <div>
              <div class="billing-plan-alert-title">{{ plan.title }}</div>
              <ul class="billing-plan-alert-points" *ngIf="plan.points.length > 0">
                <li *ngFor="let point of plan.points">{{ point }}</li>
              </ul>
            </div>
          </div>

          <label class="fee-vat-summary-label">Billing plan</label>
          <div class="billing-plan-fee" *ngFor="let row of plan.rows">
            <div class="billing-plan-fee-name">{{ row.fee }}</div>
            <ol class="billing-plan-steps">
              <li class="billing-plan-step" *ngFor="let step of row.steps; let index = index">
                <span class="billing-plan-step-number">{{ index + 1 }}</span>
                <div class="billing-plan-step-body">
                  <div class="billing-plan-step-label">{{ step.label }}</div>
                  <div class="billing-plan-step-title">{{ step.title }}</div>
                  <div class="billing-plan-step-detail">{{ step.detail }}</div>
                </div>
              </li>
            </ol>
          </div>
          <p class="billing-plan-footnote" *ngIf="plan.footnote">{{ plan.footnote }}</p>
        </div>

        <div class="invoice-details" *ngIf="data.invoiceDetails">
          <div class="details-grid">
            <div class="detail-card">
              <div class="detail-label">Corporate</div>
              <div class="detail-value">{{ data.invoiceDetails.corporate }}</div>
            </div>
            <div class="detail-card">
              <div class="detail-label">Address</div>
              <div class="detail-value">{{ data.invoiceDetails.address }}</div>
            </div>
            <div class="detail-card">
              <div class="detail-label">City</div>
              <div class="detail-value">{{ data.invoiceDetails.city }}</div>
            </div>
            <div class="detail-card">
              <div class="detail-label">Country</div>
              <div class="detail-value">{{ data.invoiceDetails.country }}</div>
            </div>
            <div class="detail-card" *ngIf="shouldDisplaySid()">
              <div class="detail-label">SDI</div>
              <div class="detail-value">{{ data.invoiceDetails.sdi }}</div>
            </div>
          </div>

          <div class="fee-vat-summary" *ngIf="data.feeVatSummary && data.feeVatSummary.length > 0">
            <label class="fee-vat-summary-label">Selected fees VAT rates</label>
            <table class="fee-vat-summary-table">
              <thead>
                <tr>
                  <th>Fee</th>
                  <th>Billing code</th>
                  <th>VAT (%)</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let fee of data.feeVatSummary">
                  <td>{{ fee.name }}</td>
                  <td>{{ fee.billing_code || '—' }}</td>
                  <td>{{ formatVatRate(fee.vat_rate) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="vat-section" *ngIf="data.showGlobalVatInput">
            <label class="vat-label">VAT Configuration in %</label>
            <input
              type="number"
              min="0"
              step="0.01"
              class="vat-input"
              [class.vat-input-error]="errorMessage"
              [(ngModel)]="data.invoiceDetails.vatRate"
              placeholder="Ex: 0, 2.1, 5.5, 10, 20..."
              (input)="onVatRateChange($event)">
            <div class="error-message" *ngIf="errorMessage">
              {{ errorMessage }}
            </div>
          </div>
        </div>
      </div>
      
      <div mat-dialog-actions class="dialog-actions">
        <button 
          mat-flat-button 
          [color]="data.cancelColor || 'warn'"
          class="bg-error text-white"
          (click)="onCancel()">
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button 
          mat-flat-button 
          [color]="data.confirmColor || 'primary'"
          class="bg-primary text-white"
          [disabled]="!isValid"
          (click)="onConfirm()"
          cdkFocusInitial>
          {{ data.confirmText || 'Create the invoice' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 24px;
      text-align: center;
      background: var(--bg-color, #ffffff);
      color: var(--text-color, #333333);
    }

    .dialog-title {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 16px;
      color: var(--text-color, #333333);
    }

    .billing-plan {
      text-align: left;
      margin-bottom: 20px;
    }

    .billing-plan-alert {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      background: var(--ap-color-warning-light);
      border: 1px solid var(--ap-color-warning);
      border-radius: 8px;
      padding: 12px 14px;
      margin-bottom: 14px;
      font-size: 13px;
      color: var(--ap-color-warning-ink);
    }

    .billing-plan-alert-icon {
      flex: 0 0 auto;
      width: 20px;
      height: 20px;
      margin-top: 1px;
    }

    .billing-plan-alert-title {
      font-weight: 600;
      font-size: 14px;
    }

    .billing-plan-alert-points {
      margin: 6px 0 0;
      padding-left: 18px;
    }

    .billing-plan-alert-points li {
      margin-bottom: 2px;
    }

    .billing-plan-fee {
      border: 1px solid var(--border-color, #e0e0e0);
      border-radius: 8px;
      overflow: hidden;
      margin-bottom: 10px;
    }

    .billing-plan-fee-name {
      background: var(--card-bg, #f9f9f9);
      border-bottom: 1px solid var(--border-color, #e0e0e0);
      padding: 8px 12px;
      font-size: 13px;
      font-weight: 600;
      color: var(--text-color, #333333);
    }

    .billing-plan-steps {
      list-style: none;
      margin: 0;
      padding: 4px 12px;
    }

    .billing-plan-step {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      padding: 8px 0;
    }

    .billing-plan-step + .billing-plan-step {
      border-top: 1px dashed var(--border-color, #e0e0e0);
    }

    .billing-plan-step-number {
      flex: 0 0 auto;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: var(--ap-color-primary);
      color: var(--ap-color-on-primary);
      font-size: 12px;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-top: 1px;
    }

    .billing-plan-step-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--text-muted, #666666);
    }

    .billing-plan-step-title {
      font-size: 13px;
      font-weight: 600;
      color: var(--text-color, #333333);
    }

    .billing-plan-step-detail {
      margin-top: 2px;
      font-size: 12px;
      color: var(--text-muted, #666666);
    }

    .billing-plan-footnote {
      margin: 8px 0 0;
      font-size: 11px;
      color: var(--text-muted, #666666);
    }

    .message-card {
      background: var(--card-bg, #ffffff);
      border: 1px solid var(--border-color, #e0e0e0);
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 20px;
      font-size: 14px;
      color: var(--text-secondary, #666666);
    }

    .pennylane-current-target {
      text-align: left;
      margin-bottom: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .pennylane-target-label {
      font-size: 13px;
      color: var(--text-muted, #888888);
      font-weight: 500;
    }

    .pennylane-target-value {
      display: inline-block;
      font-size: 13px;
      font-weight: 600;
      color: #5D87FF;
      background: rgba(93, 135, 255, 0.12);
      border: 1px solid rgba(93, 135, 255, 0.35);
      border-radius: 999px;
      padding: 4px 10px;
    }

    .invoice-details {
      margin-top: 20px;
    }

    .details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 20px;
    }

    .detail-card {
      background: var(--card-bg, #ffffff);
      border: 1px solid var(--border-color, #e0e0e0);
      border-radius: 8px;
      padding: 12px;
      text-align: left;
    }

    .detail-label {
      font-size: 12px;
      color: var(--text-muted, #999999);
      margin-bottom: 4px;
      text-transform: uppercase;
      font-weight: 500;
    }

    .detail-value {
      font-size: 14px;
      color: var(--text-color, #333333);
      font-weight: 500;
      word-wrap: break-word;
    }

    .vat-section {
      text-align: left;
    }

    .pennylane-label {
      display: block;
      font-size: 14px;
      color: var(--text-color, #333333);
      margin-bottom: 8px;
      font-weight: 500;
    }

    .pennylane-select-field {
      width: 100%;
      margin-bottom: 2px;
    }

    .pennylane-select-field.pennylane-select-error ::ng-deep .mdc-notched-outline__leading,
    .pennylane-select-field.pennylane-select-error ::ng-deep .mdc-notched-outline__notch,
    .pennylane-select-field.pennylane-select-error ::ng-deep .mdc-notched-outline__trailing {
      border-color: #f44336 !important;
      border-width: 2px !important;
    }

    .pennylane-select-field.pennylane-select-error ::ng-deep .mat-mdc-select-trigger {
      color: #f44336;
    }

    .fee-vat-summary {
      text-align: left;
      margin-top: 8px;
    }

    .fee-vat-summary-label {
      display: block;
      font-size: 14px;
      color: #4CAF50;
      margin-bottom: 8px;
      font-weight: 500;
    }

    .fee-vat-summary-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }

    .fee-vat-summary-table th,
    .fee-vat-summary-table td {
      border: 1px solid var(--border-color, #e0e0e0);
      padding: 8px 10px;
      text-align: left;
    }

    .fee-vat-summary-table th {
      background: var(--card-bg, #f9f9f9);
      font-weight: 600;
      color: var(--text-muted, #666666);
    }

    .vat-label {
      display: block;
      font-size: 14px;
      color: #4CAF50;
      margin-bottom: 8px;
      font-weight: 500;
    }

    .vat-input {
      background: var(--input-bg, #ffffff);
      border: 2px solid #4CAF50;
      border-radius: 8px;
      padding: 12px;
      font-size: 14px;
      color: var(--text-color, #333333);
      width: 100%;
      box-sizing: border-box;
      outline: none;
      transition: border-color 0.3s ease;
    }

    .vat-input:focus {
      border-color: #2E7D32;
      box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
    }

    .vat-input-error {
      border-color: #f44336 !important;
    }

    .error-message {
      color: #f44336;
      font-size: 12px;
      margin-top: 8px;
      text-align: left;
      font-weight: 500;
    }

    .dialog-actions {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-top: 24px;
      padding: 0;
    }

    .confirm-btn:disabled {
      background-color: #cccccc !important;
      cursor: not-allowed;
      opacity: 0.6;
    }

    :host-context(.dark-theme) .dialog-container {
      --bg-color: #1a2537;
      --text-color: #ffffff;
      --card-bg: #1a2537;
      --border-color: #404040;
      --text-secondary: #cccccc;
      --text-muted: #888888;
    }

    :host-context(.dark-theme) .dialog-title {
      color: #ffffff;
    }

    :host-context(.dark-theme) .message-card {
      background: #1a2537;
      border-color: #404040;
      color: #cccccc;
    }

    :host-context(.dark-theme) .detail-card {
      background: #1a2537;
      border-color: #404040;
    }

    :host-context(.dark-theme) .detail-value {
      color: #ffffff;
    }
  `]
})
export class ConfirmationCreationDraftInvoiceComponent {
  validFrenchVatRates = [0, 0.9, 1.05, 1.75, 2.1, 4, 5, 5.5, 6, 6.5, 8.5, 9.2, 10, 13, 15.385, 16, 19.6, 20];
  errorMessage: string = '';
  pennylaneErrorMessage: string = '';
  isValid: boolean = true;

  constructor(
    public dialogRef: MatDialogRef<ConfirmationCreationDraftInvoiceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmationCreationDraftInvoiceData
  ) {
    this.validateForm();
  }

  onVatRateChange(event: any): void {
    this.data.invoiceDetails!.vatRate = event.target.value;
    this.validateForm();
  }

  onPennylaneChange(): void {
    this.validateForm();
  }

  validateVatRate(): void {
    if (!this.data.showGlobalVatInput) {
      this.errorMessage = '';
      return;
    }

    const vatValue = parseFloat(this.data.invoiceDetails?.vatRate as string);

    if (isNaN(vatValue)) {
      this.errorMessage = 'Please enter a valid VAT rate';
      return;
    }

    if (!this.validFrenchVatRates.includes(vatValue)) {
      this.errorMessage = 'Invalid VAT rate! Accepted rates: 0%, 0.9%, 1.05%, 1.75%, 2.1%, 4%, 5%, 5.5%, 6%, 6.5%, 8.5%, 9.2%, 10%, 13%, 15.385%, 16%, 19.6%, 20%';
      return;
    }

    this.errorMessage = '';
  }

  private validatePennylaneSelection(): void {
    if (this.data.pennylaneOptions && this.data.pennylaneOptions.length > 0 && !this.data.selectedPennylaneCountry) {
      this.pennylaneErrorMessage = 'Please choose a Pennylane destination';
      return;
    }
    this.pennylaneErrorMessage = '';
  }

  private validateForm(): void {
    this.validateVatRate();
    this.validatePennylaneSelection();
    this.isValid = !this.errorMessage && !this.pennylaneErrorMessage;
  }

  onConfirm(): void {
    if (this.isValid) {
      const result: InvoiceCreationResult = {
        confirmed: true,
        pennylane_country: this.data.selectedPennylaneCountry ?? null
      };
      if (this.data.showGlobalVatInput) {
        result.vatRate = parseFloat(this.data.invoiceDetails?.vatRate as string);
      }
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    const result: InvoiceCreationResult = {
      confirmed: false
    };
    this.dialogRef.close(result);
  }

  formatVatRate(vatRate: number | null | undefined): string {
    if (vatRate === null || vatRate === undefined || Number.isNaN(Number(vatRate))) {
      return '—';
    }
    return `${vatRate}%`;
  }

  getVisiblePennylaneCountryLabel(): string {
    const selected = (this.data.selectedPennylaneCountry || '').toString().trim();
    if (selected) {
      return selected.charAt(0).toUpperCase() + selected.slice(1);
    }
    return this.data.pennylaneCountryLabel || 'Current workspace';
  }

  shouldDisplaySid(): boolean {
    const sdi = (this.data.invoiceDetails?.sdi || '').toString().trim();
    if (!sdi) {
      return false;
    }
    const targetCountry = (this.data.selectedPennylaneCountry || this.data.pennylaneCountryLabel || '').toString().trim().toLowerCase();
    return targetCountry === 'italy';
  }
}
